import parse from "html-react-parser"
import { motion } from "framer-motion"
import hash from "@emotion/hash"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import { usePage } from "contexts/Page"

import { fullGrid } from "css/grid"
import { headline100, surtitle100 } from "css/text"
import { contrastMode } from "css/a11y"

import Video, { cssVideo, cssContainer, cssPoster, cssFallback } from "components/media/VideoCLD"
import Image from "components/media/ImageCLD"
import TextBlock from "./TextBlock"
import Roller from "./Roller"
import ReadingInfo from "components/reading-info/ReadingInfo"
import getMediaQuery from "css/breakpoints"
import { cssVars } from "components/nav-v2/Wrapper"

export const kickerCN = `css-${hash("cvroll-heading:kicker")}`
export const subtitleCN = `css-${hash("cvroll-heading:subtitle")}`

const Root = styled(motion.section)`
  ${fullGrid}
  grid-template-rows: 80px min-content min-content min-content min-content;
  height: fit-content;
  & > :nth-last-child(1) {
    margin-block-end: 100px;
  }

  &.norollo {
    min-height: calc(100svh - var(${cssVars.main.safe}));
    grid-template-rows: 80px auto 1fr;
  }
`

const MediaContainer = styled(motion.div)`
  height: 100vh;
  width: 100%;
  grid-column: doc;
  grid-row: 1 / -1;
  /* overflow: hidden; */
  pointer-events: none;
  position: sticky;
  top: 0;
`

const VideoContainer = styled(motion.div)`
  height: 100%;
  width: 100%;

  & .${cssContainer} {
    z-index: -1;
    height: 100%;

    & .${cssVideo}, .${cssPoster}, .${cssFallback} {
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`

const ImageContainer = styled(motion.div)`
  height: 100%;
  width: 100%;
  position: sticky;
  top: 0;

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const Hgroup = styled(motion.hgroup)`
  height: fit-content;
  grid-column: 2 / span 6;
  grid-row: 2;
  display: grid;
  grid-template-rows: [kicker subtitle] min-content [mid-line] min-content [title];
  z-index: 0;
  color: rgb(var(--pure-white));

  ${getMediaQuery("m")} {
    grid-column: 2 / span 8;
  }

  & .${kickerCN} {
    ${surtitle100}
    grid-row: kicker / mid-line;
    text-wrap: pretty;
    text-shadow: 0px 0px 12px rgba(0, 0, 0, 0.05);
  }

  & .${subtitleCN} {
    ${surtitle100}
    grid-row: subtitle / mid-line;
    text-wrap: pretty;
    text-shadow: 0px 0px 12px rgba(0, 0, 0, 0.05);
  }

  & h1 {
    grid-column: 1;
    line-height: 1.2 !important;
  }

  .norollo & {
    margin-block-end: max(100px, 30vh);
  }
`

const Title = styled(motion.h1)`
  ${headline100}
  position: relative;
  height: fit-content;
  color: rgb(var(--pure-white));
  z-index: 0;
  grid-column: 2 / span 6;
  grid-row: 2;
  ${contrastMode}
  margin-block-end: 100px;
  line-height: 1.2 !important;

  ${getMediaQuery("m")} {
    grid-column: 2 / span 8;
  }

  .norollo & {
    margin-block-end: max(100px, 30vh);
  }
`

const Overlay = styled.div`
  position: relative;
  grid-area: 1 / 1 / -1 / -1;
  width: 100%;
  height: 100%;
  background: rgba(255 0 0 / 0.5);
  background: linear-gradient(45deg, rgba(var(--degcolo, 0 0 0) / var(--degopa, 0.3)) 40%, transparent);
`

function ImageItem({ image }) {
  const console = useConsole()

  return (
    <ImageContainer>
      <Image {...image} sizes='100vw' />
    </ImageContainer>
  )
}

function VideoItem({ video }) {
  const console = useConsole()

  return <VideoContainer>{video && <Video autoPlay loop noButton animate {...video} sizes='100vw' />}</VideoContainer>
}

export default function Coveroller({
  media,
  chapo,
  cta,
  heading,
  paragraph_blocks,
  roller_title,
  meta,
  sub_heading,
  sub_heading_color,
  items,
  overlay,
  ...props
}) {
  const console = useConsole()
  const { is_reading_info, reading_info } = usePage()

  const isRollo = items?.length

  const variants = {
    hide: {},
    show: {
      transition: {
        delay: 0.5,
        staggerChildren: 0.25,
      },
    },
  }

  const itemsVariants = {
    hide: { y: "80px", opacity: 0 },
    show: { y: "0px", opacity: 1, transition: { ease: "easeOut", duration: 0.5 } },
  }

  return (
    <>
      <Root className={isRollo ? "" : "norollo"} variants={variants} initial='hide' animate='show'>
        <MediaContainer>
          {media[0]?.video_cld || media[1]?.video_cld ? (
            <VideoItem video={media[0]?.video_cld ?? media[1]?.video_cld} />
          ) : media[0]?.image_cld || media[1]?.image_cld ? (
            <ImageItem image={media[0]?.image_cld ?? media[1]?.image_cld} />
          ) : null}
        </MediaContainer>
        {overlay?.active ? <Overlay style={{ "--degcolo": overlay?.color, "--degopa": overlay?.opacity }} /> : null}
        {(heading?.kicker || heading?.subtitle) && heading?.title ? (
          <Hgroup variants={itemsVariants}>
            {heading?.kicker && <span className={kickerCN}>{parse(heading.kicker)}</span>}
            <Title as={meta.index === 0 ? "h1" : "h2"}>{parse(heading.title)}</Title>
            {heading?.subtitle && <p className={subtitleCN}>{parse(heading.subtitle)}</p>}
          </Hgroup>
        ) : heading?.title ? (
          <Title as={meta.index === 0 ? "h1" : "h2"} variants={itemsVariants}>
            {parse(heading.title)}
          </Title>
        ) : null}
        <TextBlock
          chapo={chapo}
          variants={itemsVariants}
          sub_heading={sub_heading}
          sub_heading_color={sub_heading_color}
          paragraph_blocks={paragraph_blocks}
          cta={cta}
        />
        {isRollo ? <Roller roller_title={roller_title} items={items} variants={itemsVariants} /> : null}
      </Root>
      {is_reading_info && meta.index === 0 ? <ReadingInfo {...reading_info} /> : null}
    </>
  )
}
